import { Button, Inline, Stack, Text } from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	Form,
	SubmissionError,
	TextAreaInputField,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import * as Validator from "yup"
import { useNavigate } from "../router-utils"
import { XHR } from "./store"

const validationSchema = Validator.object({
	source_record_id: Validator.number()
		.typeError("Please enter a numeric source record id.")
		.integer("Please enter a whole-number record id.")
		.min(1, "Please enter a valid source record id.")
		.required("Source record id is required."),
	simulated_inbound_whatsapp_message: Validator.string().nullable(),
})

const initialValues = {
	source_record_id: "",
	simulated_inbound_whatsapp_message: "",
}

export function DryRunDialog({
	automationId,
	sourceName,
}: {
	automationId: number
	sourceName: string
}) {
	const xhr = useXHR()
	const navigate = useNavigate()
	const [isOpen, openDialog, closeDialog] = useDialog()

	return (
		<>
			<Button level="secondary" onClick={openDialog}>
				Run Dry Run
			</Button>
			<Dialog open={isOpen} onClose={closeDialog} title="Run Dry Run" md>
				<Form<typeof initialValues>
					initialValues={initialValues}
					validate={validateFormValues(validationSchema)}
					onSubmit={withServerErrors(async (values) => {
						const response = await XHR(xhr).dryRun(automationId, {
							source_record_id: Number(values.source_record_id),
							simulated_inbound_whatsapp_message:
								values.simulated_inbound_whatsapp_message.trim() || null,
						})

						showSnackbar(
							`Dry run #${response.data.id} completed. Opening Runs so you can inspect the trace.`
						)
						closeDialog()
						navigate("/automations/:automationId/runs", {
							params: {
								automationId: String(automationId),
							},
						})
					})}
					subscription={{ submitting: true }}
				>
					{({ submitting, handleSubmit }) => (
						<form noValidate onSubmit={handleSubmit}>
							<Dialog.Body>
								<Stack gap="4">
									<Text color="muted">
										Choose a real {sourceName} record to simulate this
										automation against. We will evaluate the automation and
										record a trace, but we will not send messages or create
										tasks.
									</Text>
									<TextInputField
										name="source_record_id"
										type="text"
										label="Source Record Id"
										placeholder="123"
										required
									/>
									<TextAreaInputField
										name="simulated_inbound_whatsapp_message"
										label="Simulated Inbound WhatsApp Reply"
										placeholder="Optional. Useful when this automation waits for a reply and later branches on that message."
										rows={3}
									/>
									<SubmissionError />
								</Stack>
							</Dialog.Body>
							<Dialog.Footer>
								<Inline gap="3" justifyContent="end">
									<Button
										level="secondary"
										onClick={closeDialog}
										disabled={submitting}
									>
										Cancel
									</Button>
									<Button type="submit" disabled={submitting}>
										{submitting ? "Running..." : "Run Dry Run"}
									</Button>
								</Inline>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}
